import { ProfileEditor } from '../curves/ProfileEditor.js';
import { partPaths, type ShapeEditorProps } from './doc-context.js';
import { arr, Bool, bool, Num, num, obj, OptionalSection, patch, Pick, PickList, Section, str } from './fields.js';
import { points2 } from './LatheEditor.js';

/** `cloth`: a fabric piece cut from a 2D pattern outline. */
export function ClothEditor({ shape, onChange, doc, partPath }: ShapeEditorProps) {
  const set = (key: string, v: unknown) => onChange(patch(shape, key, v));
  const parts = partPaths(doc, partPath);
  const collide = arr<string>(shape.collide);
  return (
    <>
      <Section
        title="Pattern"
        hint="The pattern piece in meters: x runs across the cloth, y is the distance from the attached edge (0 = shoulders / waist / pole, larger = the hem). Each horizontal line must cross the outline once."
      >
        <ProfileEditor closed flipY axes={['X (across)', 'Y (down from edge)']} points={points2(shape.outline)} onChange={(p) => set('outline', p)} />
      </Section>
      <Num label="Resolution" hint="Grid spacing (m); 0 = 1/24 of the piece's longest side." min={0} step={0.005} value={num(shape.resolution)} fallback={0} onChange={(v) => set('resolution', v)} />
      <Num
        label="Bend"
        hint="Curl the flat piece around a vertical axis by this many degrees (a cape round the back, a skirt panel round the hips; 360 closes a tube)."
        step={5}
        min={0}
        max={360}
        value={num(shape.bend)}
        fallback={0}
        onChange={(v) => set('bend', v)}
      />
      <Bool
        label="Bend back"
        hint="Curl direction: on = the edges curl forward (-Z) around a back (capes, cloaks); off = backwards (+Z) around a front (aprons, tabards)."
        value={bool(shape.bendBack)}
        fallback
        onChange={(v) => set('bendBack', v)}
      />
      <Num label="Thickness" hint="Fabric thickness (m): 0 = single sheet (use a double-sided material)." min={0} step={0.001} value={num(shape.thickness)} fallback={0} onChange={(v) => set('thickness', v)} />
      <OptionalSection
        title="Wrap"
        hint="Shrink-wrap the attached band onto another part's surface (shoulders under a cape, hips under a waistband, a pole under a banner)."
        value={obj(shape.wrap)}
        make={() => ({ target: '', band: 0.2 })}
        onChange={(v) => set('wrap', v)}
      >
        {(w, setW) => (
          <>
            <Pick label="Target" hint="Part name or path to wrap onto." none="Choose a part…" options={parts} value={str(w.target) || undefined} onChange={(v) => setW('target', v ?? '')} />
            <Num label="Band" hint="How much of the piece (from the attached edge) wraps: 0..1 of its length." min={0} max={1} step={0.05} value={num(w.band)} fallback={0.2} onChange={(v) => setW('band', v)} />
            <Num label="Offset" hint="Gap kept above the surface (m)." min={0} step={0.001} value={num(w.offset)} fallback={0.006} onChange={(v) => setW('offset', v)} />
          </>
        )}
      </OptionalSection>
      <PickList
        label="Collide"
        hint="Other parts the hanging cloth must stay outside of (clothes, armor, belts under a cape), besides the wrap target."
        options={parts}
        value={collide.length ? collide : undefined}
        onChange={(v) => set('collide', v)}
      />
    </>
  );
}
